import React, { useEffect, useRef, useState } from 'react';
import { DealCard } from '../DealCard';
import SectionHeader from './SectionHeader';
import { API } from '../../config/apiBase';

const LIMITE = 10;

/**
 * Quantidade mínima para uma seção existir, tirada do layout em que ela é vista:
 *
 *   trilho: 5, uma fileira completa na grade de 5 colunas do `xl`. Com menos, o
 *           desktop mostra buracos no fim da fileira e o celular, um trilho que
 *           acaba antes de começar.
 *   lista:  4, duas fileiras completas da grade de 2 colunas.
 *
 * Abaixo disso a seção não aparece. A loja continua acessível pelos atalhos,
 * pelo rodapé e pela busca; só não ganha uma vitrine própria meio vazia.
 */
export const MINIMO_POR_LAYOUT = { trilho: 5, lista: 4 };

/**
 * Trilho de ofertas: uma seção editorial alimentada por uma consulta REAL de
 * `/api/produtos`. Não existe seção montada com dado inventado.
 *
 * Carrega só quando chega perto da tela. As consultas por marketplace são
 * classificadas em Python no backend e custam de 70 a 500 ms cada; buscar tudo
 * na abertura da Home cobraria esse custo de quem nem rola a página.
 *
 * Sem resultado, a seção some. Seção vazia na vitrine é pior que seção nenhuma.
 *
 * Seção editorial só destaca oferta que dá para comprar. O backend marca cada
 * deal com `purchase_available`, e o trilho respeita esse campo em vez de
 * reimplementar a regra. Sem isso, a seção da Amazon destacaria os deals sem
 * link de produto, que o filtro por loja ainda devolve pelo nome da loja.
 *
 * Layout: trilho com rolagem horizontal até `lg`; a partir daí vira grade de 4
 * colunas (5 em `xl`) e os itens que não cabem ficam para o "Ver todas".
 */
// `layout="lista"`: linhas compactas em duas colunas, para quebrar o ritmo das
// grades de cards e caber mais itens por altura.
// `superficie`: nome da superfície para a coleta da inteligência (ex.: home_maiores_descontos).
export default function DealRail({ titulo, subtitulo, query, acao, excluir = [], carregarJa = false, testId, layout = 'trilho', superficie = '' }) {
    const [deals, setDeals] = useState(null);
    const [visivel, setVisivel] = useState(carregarJa);
    const ref = useRef(null);

    useEffect(() => {
        if (visivel) return undefined;
        const el = ref.current;
        if (!el || typeof IntersectionObserver === 'undefined') { setVisivel(true); return undefined; }
        const obs = new IntersectionObserver((entradas) => {
            if (entradas.some((e) => e.isIntersecting)) { setVisivel(true); obs.disconnect(); }
        }, { rootMargin: '600px 0px' });
        obs.observe(el);
        return () => obs.disconnect();
    }, [visivel]);

    useEffect(() => {
        if (!visivel || !query) return undefined;
        let ativo = true;
        // Pede o dobro: parte pode vir sem destino de compra e sair da lista.
        fetch(`${API}/produtos?${query}&limit=${LIMITE * 2 + excluir.length}`)
            .then((r) => r.json())
            .then((d) => {
                if (!ativo) return;
                const lista = (Array.isArray(d?.deals) ? d.deals : [])
                    .filter((x) => x.purchase_available !== false && !excluir.includes(x.deal_id));
                setDeals(lista.slice(0, LIMITE));
            })
            .catch(() => { if (ativo) setDeals([]); });
        return () => { ativo = false; };
        // `excluir` muda de identidade a cada render do pai; o que importa é a consulta.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visivel, query]);

    const minimo = MINIMO_POR_LAYOUT[layout] || MINIMO_POR_LAYOUT.trilho;
    if (deals && deals.length < minimo) return null;

    const itemClasse = (i) => [
        // No celular a largura acompanha a tela: sempre dois cards e um pedaço do
        // terceiro, de 360 a 430 px, sinal claro de que o trilho continua.
        'w-[43vw] max-w-[196px] sm:w-[210px] sm:max-w-none md:w-[228px] shrink-0 snap-start lg:w-auto',
        i === 4 ? 'lg:hidden xl:block' : '',
        i > 4 ? 'lg:hidden' : '',
    ].join(' ');

    if (layout === 'lista') {
        return (
            <section ref={ref} className="sv-section" data-testid={testId} aria-labelledby={testId ? `${testId}-titulo` : undefined}>
                <SectionHeader titulo={titulo} subtitulo={subtitulo} acao={acao} id={testId ? `${testId}-titulo` : undefined} />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
                    {deals === null
                        ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="sv-skeleton rounded-2xl h-[108px]" aria-hidden="true" />)
                        : deals.slice(0, 8).map((deal, i) => (
                            <div key={deal.deal_id} className={i >= 4 ? 'hidden sm:block' : ''}>
                                <DealCard deal={deal} variant="row" exposicao={superficie ? { surface: superficie, position: i + 1, section: testId } : null} />
                            </div>
                        ))}
                </div>
            </section>
        );
    }

    return (
        <section ref={ref} className="sv-section" data-testid={testId} aria-labelledby={testId ? `${testId}-titulo` : undefined}>
            <SectionHeader titulo={titulo} subtitulo={subtitulo} acao={acao} id={testId ? `${testId}-titulo` : undefined} />
            <div className="sv-rail flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory -mx-4 px-4 pb-2 lg:mx-0 lg:px-0 lg:pb-0 lg:grid lg:grid-cols-4 xl:grid-cols-5 lg:gap-5 lg:overflow-visible">
                {deals === null
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className={itemClasse(i)} aria-hidden="true">
                            {/* No celular o card real tem a foto quadrada (largura do item) mais
                                145 px de texto; o esqueleto 3:4,4 ficava 59 a 73 px mais baixo e
                                empurrava a Home quando o trilho carregava. */}
                            <div className="sv-skeleton rounded-2xl aspect-[3/4.4] max-sm:!aspect-auto max-sm:h-[calc(min(43vw,196px)+145px)]" />
                        </div>
                    ))
                    : deals.map((deal, i) => (
                        <div key={deal.deal_id} className={itemClasse(i)}>
                            <DealCard deal={deal} variant="tile" exposicao={superficie ? { surface: superficie, position: i + 1, section: testId } : null} />
                        </div>
                    ))}
            </div>
        </section>
    );
}
